import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { translations } from "../i18n";
import api, { ACTIVE_WS_KEY } from "../lib/api";
import { isGoogleCallbackPath } from "../lib/auth";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("os_theme") || "dark");
  const [lang, setLang] = useState(() => {
    // ?lang= (used by SEO hreflang alternates) wins, then a saved manual preference,
    // else English (default). First-time visitors get auto-detected by country below.
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      if (q === "fr" || q === "en") return q;
    } catch { /* noop */ }
    return localStorage.getItem("os_lang") || "en";
  });
  // Whether the language was already pinned at mount (saved pref or ?lang=). If not,
  // we auto-detect the locale from the visitor's country once (French-speaking → fr).
  const langPinnedRef = useRef((() => {
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      if (q === "fr" || q === "en") return true;
    } catch { /* noop */ }
    return !!localStorage.getItem("os_lang");
  })());
  const [period, setPeriod] = useState("month");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [entityHealth, setEntityHealth] = useState({});
  const [focusSummary, setFocusSummary] = useState({});
  const [toasts, setToasts] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(
    () => localStorage.getItem(ACTIVE_WS_KEY) || ""
  );

  // The workspace the user is currently viewing (defaults to their own/personal).
  const activeWorkspace =
    workspaces.find((w) => w.id === activeWorkspaceId) ||
    workspaces.find((w) => w.is_personal) ||
    workspaces[0] ||
    null;
  const wsRole = activeWorkspace?.role || "owner";
  const wsScoped = activeWorkspace ? (!activeWorkspace.is_personal && (activeWorkspace.scope === "entities" || activeWorkspace.scope === "spaces")) : false;
  const wsSpaceScoped = activeWorkspace ? (!activeWorkspace.is_personal && activeWorkspace.scope === "spaces") : false;
  const canManageTeam = wsRole === "owner" || wsRole === "super_admin" || wsRole === "admin";
  const canEdit = wsRole !== "viewer";

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((tt) => tt.id !== id));
  }, []);

  const toast = useCallback((message, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, message, variant: opts.variant || "default" }]);
    setTimeout(() => setToasts((prev) => prev.filter((tt) => tt.id !== id)), opts.duration || 4500);
    return id;
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") root.classList.add("light");
    else root.classList.remove("light");
    localStorage.setItem("os_theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("os_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // First-visit auto language: if the user never chose a language (and no ?lang=),
  // detect their country server-side and switch to French for French-speaking
  // countries. Default stays English. Runs once; a manual toggle takes precedence.
  useEffect(() => {
    if (langPinnedRef.current) return;
    let cancelled = false;
    api.get("/geo/locale")
      .then(({ data }) => {
        if (cancelled || langPinnedRef.current) return;
        if (data?.locale === "fr" || data?.locale === "en") setLang(data.locale);
      })
      .catch(() => { /* best-effort: keep English default */ });
    return () => { cancelled = true; };
  }, []);

  const t = useCallback(
    // Fall back to English (the default UI language) for any missing key, then the raw key.
    (key) => translations[lang][key] ?? translations.en[key] ?? translations.fr[key] ?? key,
    [lang]
  );

  const loadEntities = useCallback(async () => {
    try {
      const { data } = await api.get("/entities");
      setEntities(data);
    } catch {
      setEntities([]);
    }
    try {
      const { data } = await api.get("/entity-health");
      const map = {};
      data.forEach((h) => { map[h.entity_id] = h; });
      setEntityHealth(map);
    } catch {
      setEntityHealth({});
    }
    try {
      const { data } = await api.get("/dashboard/focus-summary");
      setFocusSummary(data || {});
    } catch {
      setFocusSummary({});
    }
  }, []);

  // Lightweight refresh of just the per-entity focus counts (sidebar / Group HQ
  // chips) — call after a task/goal/project mutation without reloading everything.
  const refreshFocus = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/focus-summary");
      setFocusSummary(data || {});
    } catch { /* keep previous counts on failure */ }
  }, []);

  // Lightweight refresh of just the entity-health dots (sidebar / Group HQ) — call
  // after any data mutation (invoice paid, rent paid, task done…) without a reload.
  const refreshEntityHealth = useCallback(async () => {
    try {
      const { data } = await api.get("/entity-health");
      const map = {};
      data.forEach((h) => { map[h.entity_id] = h; });
      setEntityHealth(map);
    } catch { /* keep previous health on failure */ }
  }, []);

  const loadWorkspaces = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      // If the stored active workspace is no longer accessible, fall back to personal.
      const stored = localStorage.getItem(ACTIVE_WS_KEY);
      if (stored && !data.find((w) => w.id === stored)) {
        localStorage.removeItem(ACTIVE_WS_KEY);
        setActiveWorkspaceId("");
      }
      return data;
    } catch {
      return [];
    }
  }, []);

  const switchWorkspace = useCallback(async (workspaceId) => {
    const personal = workspaces.find((w) => w.is_personal);
    const isPersonal = !workspaceId || (personal && workspaceId === personal.id);
    if (isPersonal) {
      localStorage.removeItem(ACTIVE_WS_KEY);
      setActiveWorkspaceId("");
    } else {
      localStorage.setItem(ACTIVE_WS_KEY, workspaceId);
      setActiveWorkspaceId(workspaceId);
    }
    await loadEntities();
  }, [workspaces, loadEntities]);

  const checkAuth = useCallback(async () => {
    // The Google callback page handles its own sign-in; don't probe /auth/me here.
    if (isGoogleCallbackPath()) {
      setAuthLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadWorkspaces();
      await loadEntities();
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, [loadEntities, loadWorkspaces]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Live-refresh focus counts + entity-health dots when underlying data changes
  // (debounced so rapid sequences collapse into one fetch). Health refreshes on any
  // data mutation; focus only when tasks/goals/projects changed. Authed only.
  useEffect(() => {
    let timer;
    let focusDirty = false;
    const FOCUS_RE = /\/(goals|projects|tasks)(\/|\?|$)/;
    const onChange = (e) => {
      if (FOCUS_RE.test(e?.detail?.url || "")) focusDirty = true;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!user) return;
        refreshEntityHealth();
        if (focusDirty) { refreshFocus(); focusDirty = false; }
      }, 400);
    };
    window.addEventListener("app-data-changed", onChange);
    return () => { window.removeEventListener("app-data-changed", onChange); clearTimeout(timer); };
  }, [refreshFocus, refreshEntityHealth, user]);

  // Plan-limit (HTTP 402) prompts: toast the upgrade message from anywhere in the app.
  useEffect(() => {
    const onLimit = (e) => {
      const msg = e?.detail?.message ||
        (lang === "fr" ? "Limite de votre plan atteinte. Améliorez votre abonnement."
                       : "You've reached your plan limit. Upgrade to continue.");
      toast(msg, { variant: "error", duration: 6000 });
    };
    window.addEventListener("plan-limit", onLimit);
    return () => window.removeEventListener("plan-limit", onLimit);
  }, [toast, lang]);

  const signIn = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await loadWorkspaces();
    await loadEntities();
    return data;
  }, [loadEntities, loadWorkspaces]);

  const signUp = useCallback(async (email, password, name) => {
    // No-free-tier model: registration sends a verification email and does NOT create a
    // session. The caller shows a "check your email" screen; the trial starts after verify.
    const { data } = await api.post("/auth/register", {
      email, password, name, origin: window.location.origin,
    });
    return data;
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem(ACTIVE_WS_KEY);
    setUser(null);
    window.location.href = "/login";
  };

  // Inbox unread badge (mentions + watching) — polled while signed in
  const [inboxUnread, setInboxUnread] = useState({ unread_mentions: 0, unread_watching: 0, total: 0 });
  const [highlightAddEntity, setHighlightAddEntity] = useState(false);

  // First-run onboarding choice for a new owner: "demo" seeds sample data, "fresh"
  // keeps an empty workspace (and pulses the + entity button to guide the user).
  const chooseOnboarding = useCallback(async (choice) => {
    await api.post("/onboarding", { choice });
    setUser((u) => (u ? { ...u, needs_onboarding: false, onboarded: true } : u));
    await loadWorkspaces();
    await loadEntities();
    if (choice === "fresh") setHighlightAddEntity(true);
  }, [loadEntities, loadWorkspaces]);

  // Danger zone: wipe all workspace content and return to the onboarding choice.
  const resetWorkspace = useCallback(async () => {
    const { data } = await api.post("/account/reset-workspace");
    if (data.user) setUser(data.user);
    await loadWorkspaces();
    await loadEntities();
    return data;
  }, [loadEntities, loadWorkspaces]);

  const refreshInbox = useCallback(async () => {
    try { const { data } = await api.get("/inbox/summary"); setInboxUnread(data); } catch { /* noop */ }
  }, []);
  useEffect(() => {
    if (!user) return undefined;
    refreshInbox();
    const id = setInterval(refreshInbox, 60000);
    return () => clearInterval(id);
  }, [user, activeWorkspace?.id, refreshInbox]);

  return (
    <AppContext.Provider
      value={{
        theme,
        setTheme,
        toggleTheme: () => setTheme((p) => (p === "dark" ? "light" : "dark")),
        lang,
        setLang: (v) => { langPinnedRef.current = true; setLang(v); },
        toggleLang: () => { langPinnedRef.current = true; setLang((p) => (p === "fr" ? "en" : "fr")); },
        t,
        period,
        setPeriod,
        user,
        setUser,
        authLoading,
        entities,
        entityHealth,
        focusSummary,
        refreshFocus,
        refreshEntityHealth,
        loadEntities,
        workspaces,
        loadWorkspaces,
        activeWorkspace,
        activeWorkspaceId: activeWorkspace?.id || "",
        switchWorkspace,
        wsRole,
        wsScoped,
        wsSpaceScoped,
        canManageTeam,
        inboxUnread,
        refreshInbox,
        highlightAddEntity,
        setHighlightAddEntity,
        chooseOnboarding,
        resetWorkspace,
        canEdit,
        checkAuth,
        signIn,
        signUp,
        logout,
        toasts,
        toast,
        dismissToast,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
